import { IControl } from "Controls/Interfaces";
import { Control } from "Controls/Control";
import { ControlEventTypes } from "Core/Events/EventTypes";
import { ControlTemplate } from "Controls/Entities";
import { TextBlockCategory } from "Enums";

/**
 * ## Checkbox
 *
 * Control for displaying checkboxes.
 *
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a CheckboxControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class CheckboxControl extends Control implements IControl {
    /**
     * @hidden
     */
    protected defaultEditorElement: string = `<input type="checkbox" class="k-checkbox" />`;

    /**
     * @hidden
     */
    protected bindingProperty: string = "checked";

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-checkbox";

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        // this.jQueryElement = element.find(".k-checkbox");
        if (this.jQueryElement.prop("readonly")) {
            this.jQueryElement.prop("disabled", true);
        }
        this.jQueryElement.on("change", () => this.trigger(ControlEventTypes.Change));
        this.jQueryElement.on("focusout", e => this.trigger(ControlEventTypes.LostFocus));
    }

    /**
     * Generate a wrapper for the input element in the given controlTemplate.
     * This is used for example in CheckboxControl, where the data-binding is on the input-element
     * but the visibility binding is on the wrapper.
     * @param controlTemplate The controltemplate to be modified.
     * @hidden
     */
    protected getInputWrapper(controlTemplate: ControlTemplate): ControlTemplate {
        if (controlTemplate.element) {
            // let element = controlTemplate.element;
            // element = element.removeAttr(CONTROL_ID_ATTRIBUTE_NAME);
            // let checkBoxId = `dms-checkbox-${this.controlDefinition.ControlID}`;
            // let wrapper = $("<span>");
            // element.addClass("k-checkbox");
            // element.prop("id", checkBoxId);
            // let elem = $(element);
            // element.replaceWith(wrapper);
            // wrapper.append(elem);
            // wrapper.append(`<label class="k-checkbox-label" for="${checkBoxId}"/>`);
            // this.setDefaultEditorProperties(wrapper);
            // wrapper.removeClass("dms-control-input");
            // controlTemplate.element = wrapper;
            // controlTemplate.template = wrapper[0].outerHTML;
        }
        return controlTemplate;
    }

    /**
     * Gets template for tooltip
     * @param bindingField
     * @hidden
     */
    protected getTooltipTemplate(bindingField?: string): string {
        return `#= ${bindingField} == "true" || ${bindingField} == true ? "${this.textBlockService.get(
            "True",
            TextBlockCategory.global,
            true
        )}" : "${this.textBlockService.get("False", TextBlockCategory.global, true)}" #`;
    }

    /**
     * Get value from the control. The value is true when the checkbox is checked, otherwise false.
     * @since 4.0.0
     */
    public getValue(): boolean {
        if (this.jQueryElement.is(":checked") === true) {
            return true;
        } else if (this.jQueryElement.is(":checked") === false) {
            return false;
        }
    }

    /**
     * Set control value. Set true if the checkbox should be checked, otherwise false.
     * @param value Value to be set.
     * @since 4.0.0
     */
    public setValue(value: boolean) {
        let oldValue = this.jQueryElement.is(":checked");
        if (oldValue != value) {
            this.jQueryElement.prop("checked", value);
            this.jQueryElement.change();
        }
    }

    /**
     * Gets the display template for readonly columns and non-editable grid.
     * @hidden
     */
    public getDisplayTemplate(bindingField?: string): string {
        let field = (bindingField || "").split(`"`).join("&quot;");
        // return `<label data-dms-checkboxid="${this.controlDefinition.ControlID}" class="k-checkbox dms-checkbox-label #= (${field} ? 'dms-checked' : '') #"></label>`;
        return `<input type="checkbox" data-dms-checkboxid="${this.controlDefinition.ControlID}" class="k-checkbox dms-checkbox-label k-state-disabled" readonly="readonly"  #= (${field} ? 'checked="checked"' : '') # />`;
      }

    /**
     * Get the template for the grid group header.
     * @hidden
     */
    public getGroupHeaderTemplate(): string {
        return this.getTooltipTemplate("value");
    }

    /**
     * Get the display template for a binding field.
     * @hidden
     */
    public getDisplayTemplateForField(bindingField: string): string {
        return this.getTooltipTemplate(bindingField);
    }

    /**
     * Gets the datatype for the ViewModel (ie. "string", "number", etc.)
     * @hidden
     */
    public getModelDatatype(): string {
        return "boolean";
    }

    /**
     * Gets the default value for the model if no default value is set in controlDefinition.
     * @hidden
     */
    public getModelDefaultValue(): any {
        return false;
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     * @hidden
     */
    public getOuterElement(): JQuery {
        return this.jQueryElement;
    }

    /**
     * Dispose the control.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();

        if (this.jQueryElement != null) {
            this.jQueryElement.off("change");
            this.jQueryElement.off("focusout");
        }
    }
}
